/**
 * REGISTA AI: Main Layout Component
 * 
 * Public website layout with navigation, footer, and page wrapper.
 * Features responsive design and cinematic dark theme.
 */

import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Film, Menu, X, Github, Twitter, Linkedin } from 'lucide-react';

function Navigation() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const user = null; // Temporary - will be replaced with proper auth

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="border-b border-dark-200 bg-dark-50/80 backdrop-blur-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <Film className="w-8 h-8 text-primary-500" />
            <span className="text-xl font-display font-bold text-dark-900">
              Regista AI
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/pricing"
              className={`nav-link ${isActive('/pricing') ? 'nav-link-active' : ''}`}
            >
              Pricing
            </Link>
            <Link
              to="/templates"
              className={`nav-link ${isActive('/templates') ? 'nav-link-active' : ''}`}
            >
              Templates
            </Link>
            <Link
              to="/about"
              className={`nav-link ${isActive('/about') ? 'nav-link-active' : ''}`}
            >
              About
            </Link>
            <Link
              to="/contact"
              className={`nav-link ${isActive('/contact') ? 'nav-link-active' : ''}`}
            >
              Contact
            </Link>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <Link
                to="/dashboard"
                className="btn btn-primary btn-md"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="btn btn-ghost btn-md"
                >
                  Sign In
                </Link>
                <Link
                  to="/login"
                  className="btn btn-primary btn-md"
                >
                  Test Login
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-dark-600 hover:text-dark-900 hover:bg-dark-100"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-dark-200">
            <div className="space-y-3">
              <Link
                to="/pricing"
                className="block nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/templates"
                className="block nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Templates
              </Link>
              <Link
                to="/about"
                className="block nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/contact"
                className="block nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <div className="pt-3 border-t border-dark-200">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="block btn btn-primary btn-md w-full"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      className="block btn btn-ghost btn-md w-full"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/login"
                      className="block btn btn-primary btn-md w-full"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Test Login
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-dark-100 border-t border-dark-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Film className="w-6 h-6 text-primary-500" />
              <span className="text-lg font-display font-bold text-dark-900">
                Regista AI
              </span>
            </div>
            <p className="text-dark-600 text-sm">
              Transform text briefs into professional cinematic trailers with AI.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://twitter.com/registaai"
                className="text-dark-500 hover:text-dark-700 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://github.com/registaai"
                className="text-dark-500 hover:text-dark-700 transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/company/registaai"
                className="text-dark-500 hover:text-dark-700 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-semibold text-dark-900 mb-4">Product</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/pricing" className="text-dark-600 hover:text-dark-900">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/templates" className="text-dark-600 hover:text-dark-900">
                  Templates
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-dark-600 hover:text-dark-900">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-dark-900 mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/about" className="text-dark-600 hover:text-dark-900">
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-dark-600 hover:text-dark-900">
                  Contact
                </Link>
              </li>
              <li>
                <a href="/blog" className="text-dark-600 hover:text-dark-900">
                  Blog
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-dark-900 mb-4">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privacy" className="text-dark-600 hover:text-dark-900">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-dark-600 hover:text-dark-900">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-dark-600 hover:text-dark-900">
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-dark-200 text-center text-sm text-dark-600">
          <p>
            © {new Date().getFullYear()} Regista AI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

interface LayoutProps {
  children?: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1">
        {children || <Outlet />}
      </main>
      <Footer />
    </div>
  );
}